import { useState, useEffect } from 'react';
import { Container, Card, Form, Row, Col, Button, Table, Spinner, Badge, Modal, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FaFilter, FaFileExport, FaInfoCircle, FaSearch } from 'react-icons/fa';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import AsyncSelect from 'react-select/async';
import moment from 'moment';

import relatoriosService from '../../api/relatorios/relatoriosService';
import programasService from '../../api/programas/programasService';
import Paginacao from '../../common/pagination';
import ModalDetalhesDuplicidade from './ModalDetalhesDuplicidade';
import Select from 'react-select';

const MySwal = withReactContent(Swal);

const filterSchema = z.object({
    programa: z.union([
        z.object({
            value: z.number(),
            label: z.string()
        }),
        z.null(),
        z.undefined()
    ]).refine((val) => val && val.value, { message: "Selecione um programa" }),
    tipo_registro: z.object({
        value: z.any(),
        label: z.string()
    }).nullable().refine((val) => val && val.value, { message: "Selecione um tipo de registro" }),
    tipo_registro: z.object({
        value: z.any(),
        label: z.string()
    }).nullable().refine((val) => val && val.value, { message: "Selecione um tipo de registro" }),
    data_inicio: z.string().optional(),
    data_fim: z.string().optional(),
});

export default function RelatorioDuplicidades() {
    const { register, handleSubmit, control, watch, formState: { errors } } = useForm({
        resolver: zodResolver(filterSchema),
        defaultValues: {
            tipo_registro: null,
            data_inicio: "",
            data_fim: "",
        }
    });

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ results: [], count: 0 });
    const [page, setPage] = useState(1);
    const [options, setOptions] = useState({ tipos_registro: [] });
    
    const [showModal, setShowModal] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsData, setDetailsData] = useState(null);
    const [selectedItemName, setSelectedItemName] = useState("");

    // Modal para visualizar string "duplicado_com"
    const [showDuplicationInfoModal, setShowDuplicationInfoModal] = useState(false);
    const [duplicationInfoText, setDuplicationInfoText] = useState("");

    const [activeFilters, setActiveFilters] = useState({});

    useEffect(() => {
        loadOptions();
    }, []);

    const loadOptions = async () => {
        try {
            const opts = await relatoriosService.getOptions();
            setOptions(opts);
        } catch (error) {
            console.error("Erro ao carregar opções", error);
        }
    };

    const loadProgramas = async (inputValue) => {
        return await programasService.loadProgramas(inputValue);
    };

    const onSubmit = (formData) => {
        setActiveFilters(formData);
        setPage(1);
        fetchData(1, formData);
    };

    const fetchData = async (pageNum, filters = activeFilters) => {
        setLoading(true);
        try {
            const params = {
                page: pageNum,
                limit: 20,
                programa_id: filters.programa?.value,
                tipo_registro: filters.tipo_registro?.value,
                data_inicio: filters.data_inicio,
                data_fim: filters.data_fim,
                mode: filters.mode
            };

            Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

            const result = await relatoriosService.getDuplicidades(params);
            
            setData(result);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            MySwal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Não foi possível carregar os dados. Verifique os filtros e tente novamente.'
            });
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchData(newPage);
    };

    const handleRowClick = async (item) => {
        setSelectedItemName(item.nome);
        setShowModal(true);
        setDetailsLoading(true);
        setDetailsData(null);

        try {
            const result = await relatoriosService.getDetalhes({
                registro_id: item.id,
                tipo_registro: activeFilters.tipo_registro?.value
            });
            setDetailsData(result);
            setDetailsLoading(false);
        } catch (error) {
            setDetailsLoading(false);
            setShowModal(false);
            MySwal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Não foi possível carregar os detalhes deste registro.'
            });
        }
    };

    const parseDuplicationInfo = (text) => {
        if (!text) return [];
        
        // Split by semicolon to get different conflict groups (e.g. CPF conflict; Name conflict)
        const groups = text.split(';').map(g => g.trim()).filter(g => g);
        
        return groups.map(group => {
            // Split by ' com: ' to separate the reason/key from the values
            // doing a regex split or index search to be safe
            const separator = ' com: ';
            const idx = group.indexOf(separator);
            
            if (idx === -1) {
                return {
                    title: group,
                    items: []
                };
            }

            const title = group.substring(0, idx + separator.length).trim();
            const content = group.substring(idx + separator.length).trim();
            
            // Split items by comma, but be careful if comma appears inside content? 
            // Usually IDs and Names are separated by comma like: "ID: 103 Nome: X, ID: 204 Nome: Y"
            const items = content.split(',').map(i => i.trim()).filter(i => i);
            
            return {
                title,
                items
            };
        });
    };

    const handleOpenDuplicationInfo = (e, info) => {
        e.stopPropagation();
        setDuplicationInfoText(info || "");
        setShowDuplicationInfoModal(true);
    };

    const handleExport = async () => {
        const filters = watch();
        if (!filters.programa?.value || !filters.tipo_registro) {
            MySwal.fire({
                icon: 'warning',
                title: 'Atenção',
                text: 'Selecione pelo menos um Programa e um Tipo de Registro para exportar.'
            });
            return;
        }

        try {
            MySwal.fire({
                title: 'Gerando Exportação...',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const params = {
                programa_id: filters.programa?.value,
                tipo_registro: filters.tipo_registro?.value,
                data_inicio: filters.data_inicio,
                data_fim: filters.data_fim,
                mode: filters.mode
            };

            const blob = await relatoriosService.exportDuplicidades(params);
            
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `relatorio_duplicidades_${moment().format('YYYYMMDDHHmmss')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            MySwal.close();
            MySwal.fire({
                icon: 'success',
                title: 'Sucesso',
                text: 'Relatório exportado com sucesso!'
            });

        } catch (error) {
            MySwal.close();
            MySwal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao exportar relatório.'
            });
        }
    };


    

    return (
        <Container fluid className="p-4">
            <h1 className="h3 mb-4 text-white">
                Relatório de Duplicidades
            </h1>

            <Card className="shadow mb-4">
                <Card.Header className="py-3">
                    <h6 className="m-0 font-weight-bold text-dark">Filtros de Pesquisa</h6>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        {/* Mode Selection */}


                        <Row>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Programa <span className="text-danger">*</span></Form.Label>
                                    <Controller
                                        name="programa"
                                        control={control}
                                        render={({ field }) => (
                                            <AsyncSelect
                                                {...field}
                                                cacheOptions
                                                defaultOptions
                                                loadOptions={loadProgramas}
                                                placeholder="Selecione o Programa..."
                                                isClearable
                                            />
                                        )}
                                    />
                                    {errors.programa && <span className="text-danger small">{errors.programa.message}</span>}
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tipo de Registro <span className="text-danger">*</span></Form.Label>
                                    <Controller
                                        name="tipo_registro"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                options={options.tipos_registro?.map(opt => ({ value: opt.id, label: opt.label })) || []}
                                                placeholder="Selecione..."
                                                isClearable
                                                classNamePrefix="select"
                                            />
                                        )}
                                    />
                                    {errors.tipo_registro && <span className="text-danger small">{errors.tipo_registro.message}</span>}
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data Início</Form.Label>
                                    <Form.Control type="date" {...register("data_inicio")} />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data Fim</Form.Label>
                                    <Form.Control type="date" {...register("data_fim")} />
                                </Form.Group>
                            </Col>
                            <Col md={2} className="d-flex align-items-end mb-3">
                                <Button type="submit" variant="secondary" size="sm">
                                    <FaSearch className="me-2" /> Filtrar
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            <Card className="shadow mb-4">
                <Card.Header className="py-3 d-flex flex-row align-items-center justify-content-between">
                    <h6 className="m-0 font-weight-bold text-dark">Resultados</h6>
                    <Button variant="success" size="sm" onClick={handleExport}>
                        <FaFileExport className="me-2" /> Exportar Excel
                    </Button>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </Spinner>
                        </div>
                    ) : (
                        <>
                                <Table responsive className="custom-table mt-3">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Nome</th>
                                            <th>Programa</th>
                                            <th>Tipo</th>
                                            <th>CPF/CNPJ</th>
                                            <th>CRM/UF</th>
                                            <th>Verificar Duplicidade</th>
                                            <th>Data Criação</th>
                                            <th>Última Atualização</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.results && data.results.length > 0 ? (
                                            data.results.map((item) => (
                                                <tr 
                                                    key={`${item.id}-${item.tipo}`}
                                                    onClick={() => handleRowClick(item)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td>{item.id}</td>
                                                    <td>{item.nome}</td>
                                                    <td>{item.programa}</td>
                                                    <td>
                                                        <Badge bg="info">{item.tipo}</Badge>
                                                    </td>
                                                    <td>{item.cpf_cnpj || '--'}</td>
                                                    <td>
                                                        {item.registro ? `${item.registro}${item.registro_uf ? '/' + item.registro_uf : ''}` : '--'}
                                                    </td>
                                                    <td className="text-left">
                                                        {item.duplicado_com && (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={<Tooltip>Clique para ver detalhes do conflito</Tooltip>}
                                                            >
                                                                <Button 
                                                                    variant="outline-danger" 
                                                                    size="sm" 
                                                                    onClick={(e) => handleOpenDuplicationInfo(e, item.duplicado_com)}
                                                                >
                                                                    <FaInfoCircle className="me-1"/> Ver Motivo
                                                                </Button>
                                                            </OverlayTrigger>
                                                        )}
                                                    </td>
                                                    <td>{item.data_criacao}</td>
                                                    <td>{item.data_atualizacao}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="9" className="text-center">Nenhum registro encontrado.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            
                            {/* Pagination Logic */}
                            {data.count > 0 && (
                                <div className="mt-3">
                                     <Paginacao
                                        dados={data}
                                        limit={20}
                                        page_numb={page}
                                        loading={loading}
                                        _onClickPagina={(p) => handlePageChange(p)}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Details Modal */}
            <ModalDetalhesDuplicidade
                show={showModal}
                onHide={() => setShowModal(false)}
                loading={detailsLoading}
                data={detailsData}
                itemName={selectedItemName}
            />

            {/* Modal Simples para Mostrar duplicado_com */}
            <Modal show={showDuplicationInfoModal} onHide={() => setShowDuplicationInfoModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="h5">Detalhes da Duplicidade</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-1">Motivo do conflito:</p>
                    <div className="p-3 bg-light border rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {duplicationInfoText ? (
                            parseDuplicationInfo(duplicationInfoText).map((group, idx) => (
                                <div key={idx} className="mb-3">
                                    <h6 className="font-weight-bold text-primary mb-2">{group.title}</h6>
                                    {group.items.length > 0 ? (
                                        <ul className="list-group list-group-flush small">
                                            {group.items.map((item, i) => (
                                                <li key={i} className="list-group-item bg-transparent py-1 px-0 border-0">
                                                    • {item}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <span className="text-muted small">Sem lista detalhada.</span>
                                    )}
                                    {idx < parseDuplicationInfo(duplicationInfoText).length - 1 && <hr className="my-2"/>}
                                </div>
                            ))
                        ) : (
                            <span className="text-muted">Sem detalhes disponíveis.</span>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDuplicationInfoModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
